// Minimal rich-text editor for planner descriptions. Matches the
// Drillcut TipTap toolbar visually (bold, italic, underline, bulleted
// list, ordered list, blockquote, clear formatting, link) and produces
// HTML output, but without pulling in a 100KB editor dep. We use a
// `contentEditable` div + `document.execCommand` — yes, that API is
// formally deprecated, but every browser still ships it and it's the
// fastest path to a Drillcut-equivalent UX without TipTap. If the
// command set has to grow we can swap in TipTap later without changing
// the prop contract.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  RemoveFormatting,
  Link as LinkIcon,
} from "lucide-react";
import { DC } from "../sheets/createSlideOver/primitives";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minRows?: number;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "",
  minRows = 3,
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hasFocus, setHasFocus] = useState(false);
  // Track whether the contentEditable is empty so we can show / hide the
  // placeholder. The contenteditable browser hack — innerHTML === "<br>"
  // means "user cleared everything but the trailing line break" — is
  // included so an empty editor doesn't render as a tall stripe.
  const isEmpty =
    !value ||
    value === "<p></p>" ||
    value === "<br>" ||
    value.replace(/<[^>]*>/g, "").trim() === "";

  // Keep the DOM in sync with `value` only when the editor is *not*
  // currently focused, otherwise React would clobber the caret on every
  // keystroke as the parent re-renders.
  useEffect(() => {
    const el = ref.current;
    if (!el || hasFocus) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value;
    }
  }, [value, hasFocus]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (el) onChange(el.innerHTML);
  }, [onChange]);

  const cmd = useCallback(
    (command: string, arg?: string) => {
      ref.current?.focus();
      // `document.execCommand` is deprecated but universally supported
      // for these formatting commands and is the simplest way to keep
      // selection / undo / redo behaviour intact.
      document.execCommand(command, false, arg);
      emit();
    },
    [emit],
  );

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (!url) return;
    cmd("createLink", url);
  };

  return (
    <div
      style={{
        border: `1px solid ${hasFocus ? DC.primary : DC.borderStrong}`,
        borderRadius: 6,
        background: DC.surface,
        boxShadow: hasFocus ? "0 0 0 3px var(--bsp-blue-tint)" : undefined,
        transition: "border-color 120ms, box-shadow 120ms",
      }}
    >
      <div
        role="toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "6px 8px",
          borderBottom: `1px solid ${DC.border}`,
          background: DC.surfaceMuted,
        }}
      >
        <ToolbarBtn onClick={() => cmd("bold")} title="Bold (⌘B)">
          <Bold size={13} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => cmd("italic")} title="Italic (⌘I)">
          <Italic size={13} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => cmd("underline")} title="Underline (⌘U)">
          <Underline size={13} />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={() => cmd("insertUnorderedList")} title="Bulleted list">
          <List size={13} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => cmd("insertOrderedList")} title="Numbered list">
          <ListOrdered size={13} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => cmd("formatBlock", "blockquote")} title="Quote">
          <Quote size={13} />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn onClick={() => cmd("removeFormat")} title="Clear formatting">
          <RemoveFormatting size={13} />
        </ToolbarBtn>
        <ToolbarBtn onClick={insertLink} title="Insert link">
          <LinkIcon size={13} />
        </ToolbarBtn>
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => setHasFocus(true)}
          onBlur={() => {
            setHasFocus(false);
            emit();
          }}
          onInput={emit}
          // Ignore the React-passed children — value flows into DOM
          // via the useEffect above so the caret survives parent
          // re-renders.
          style={{
            minHeight: minRows * 22 + 24,
            padding: "10px 12px",
            outline: 0,
            font: "400 14px/1.5 var(--bsp-font-sans)",
            color: "var(--bsp-fg)",
          }}
        />
        {isEmpty && !hasFocus ? (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 12,
              pointerEvents: "none",
              color: "var(--bsp-fg-subtle)",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {placeholder}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ToolbarBtn({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      onClick={onClick}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        border: 0,
        background: "transparent",
        color: DC.greyDeep,
        borderRadius: 4,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = "var(--bsp-blue-pale)";
        (e.currentTarget as HTMLButtonElement).style.color = "var(--bsp-blue)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = "transparent";
        (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-700)";
      }}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 16,
        margin: "0 4px",
        background: DC.border,
      }}
    />
  );
}
